import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import "./CustomerNameLink.css";

/**
 * Renders a customer's name as a clickable link to the staff-side
 * Customer Detail page (`/staff/customers/:customerId`). Used across
 * every staff surface that previously displayed a static customer
 * name — removes the manual "copy id, navigate, paste" loop.
 *
 * If `customerId` is missing/falsy, falls back to a plain `<span>`
 * with the same children — preserves layout while gracefully degrading
 * for legacy bookings where the user reference may be unpopulated.
 *
 * Subtle hover affordance comes from `CustomerNameLink.css`:
 * inheriting color until hover, then underline + accent color. Keeps
 * dense tables visually quiet by default; the link affordance shows
 * on hover.
 */
const CustomerNameLink = ({
  customerId,
  children,
  className = "",
  title,
  onClick,
}) => {
  if (!customerId) {
    return (
      <span className={`customer-name-link customer-name-link--disabled ${className}`.trim()}>
        {children}
      </span>
    );
  }

  return (
    <Link
      to={`/staff/customers/${customerId}`}
      className={`customer-name-link ${className}`.trim()}
      title={title || "View customer profile"}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

CustomerNameLink.propTypes = {
  customerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
};

export default CustomerNameLink;
